import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { MessageSquare, Plus, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { MemberBar, fmtWhen } from "@/components/forum/MemberBar";
import { NewTopicDialog } from "@/components/forum/NewTopicDialog";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { memberApi } from "@/lib/api";

const iconFor = (name) => Icons[name] || MessageSquare;

export default function ForumHome() {
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const { member } = useMemberAuth();
  const navigate = useNavigate();

  const load = () => {
    memberApi.get("/forum/categories").then((r) => setCategories(r.data)).catch(() => {});
    memberApi.get("/forum/recent").then((r) => setRecent(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const onNew = () => {
    if (!member) return navigate("/forum/join?mode=register");
    setShowNew(true);
  };

  return (
    <>
      <PageHeader label="Community" title="THE FISH HOUSE FORUM">
        Where the community lives. Ask questions, share your builds and help decide where the project goes next.
      </PageHeader>

      <section className="pb-28" data-testid="forum-home">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <MemberBar />

          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-bold text-2xl text-white">Boards</h2>
            <button onClick={onNew} disabled={categories.length === 0} className="btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="forum-new-topic-btn">
              <Plus size={16} /> New Topic
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {categories.map((c, i) => {
              const Icon = iconFor(c.icon);
              return (
                <Reveal key={c.id} delay={(i % 2) * 0.06}>
                  <Link to={`/forum/category/${c.slug}`} className="group card-surface p-6 flex items-start gap-4 h-full" data-testid={`forum-category-${c.slug}`}>
                    <span className="h-12 w-12 shrink-0 grid place-items-center rounded-xl bg-brand-cyan/10 text-brand-cyan">
                      <Icon size={22} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-display font-semibold text-white text-lg group-hover:text-brand-cyan transition-colors">{c.name}</h3>
                        <ChevronRight size={18} className="text-brand-textMuted group-hover:text-brand-cyan transition-colors" />
                      </div>
                      <p className="text-brand-textSec text-sm mt-1 leading-relaxed">{c.description}</p>
                      <p className="text-brand-textMuted text-xs mt-3">
                        {c.topic_count} topic{c.topic_count === 1 ? "" : "s"} · {c.post_count} repl{c.post_count === 1 ? "y" : "ies"}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          {recent.length > 0 && (
            <div className="mt-14">
              <h2 className="font-display font-bold text-2xl text-white mb-6">Latest activity</h2>
              <div className="space-y-2">
                {recent.map((t) => (
                  <Link key={t.id} to={`/forum/topic/${t.id}`} className="flex items-center justify-between gap-4 card-surface px-5 py-3.5" data-testid={`forum-recent-${t.id}`}>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{t.title}</p>
                      <p className="text-brand-textMuted text-xs mt-0.5">by {t.author_name} · {t.reply_count} repl{t.reply_count === 1 ? "y" : "ies"}</p>
                    </div>
                    <span className="text-brand-textMuted text-xs shrink-0">{fmtWhen(t.last_activity)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {showNew && (
        <NewTopicDialog
          categories={categories}
          onClose={() => setShowNew(false)}
          onCreated={(topic) => { setShowNew(false); navigate(`/forum/topic/${topic.id}`); }}
        />
      )}
    </>
  );
}
